import { LoadingSpinner } from '@neo4j-ndl/react';
import { useLogger } from '@nx/logger/react';
import { consoleApi, getApiErrorMessage, useAuraUser, useNotifications } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { Center } from '@nx/ui';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

type ISaasMarketplaceRegistrationProps = {
  gcpToken: string;
};

export const ISaasMarketplaceRegistrationWithoutOrgSelector = ({ gcpToken }: ISaasMarketplaceRegistrationProps) => {
  const navigate = useNavigate();
  const logger = useLogger();
  const notify = useNotifications();
  const [orgIdToNavigate, setOrgIdToNavigate] = useState<string | null>(null);
  const { user } = useAuraUser();
  const { org } = consoleApi.useListOrganizationsByUserQuery(user?.id ?? skipToken, {
    selectFromResult: ({ data: orgs }) => ({ org: orgs?.find((o) => o.id === orgIdToNavigate) }),
  });

  const [registerOrder] = consoleApi.useRegisterISaasOrderMutation();

  useEffect(() => {
    registerOrder({ token: gcpToken })
      .unwrap()
      .then(({ orgId, newProjectId }) => {
        setOrgIdToNavigate(orgId);
        if (isNotNullish(newProjectId)) {
          notify.addNotification({
            timeout: 3000,
            title: 'GCP Project setup',
            description: 'Successfully setup your GCP Marketplace project',
            type: 'success',
          });
        }
      })
      .catch((err: FetchBaseQueryError | SerializedError) => {
        const message = getApiErrorMessage(err);
        logger.error(message);
        notify.addNotification({
          timeout: 5000,
          title: 'Error',
          description: message,
          type: 'warning',
        });

        void navigate('/', { replace: true });
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNotNullish(org)) {
      void navigate(`/org/${org.id}/projects`, { replace: true });
    }
  }, [org, navigate]);

  return (
    <Center className="flex-col">
      <LoadingSpinner size="large" />
      Setting up your GCP Marketplace Project
    </Center>
  );
};

export const ISaasMarketplaceRegistrationGuard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const notify = useNotifications();
  const gcpToken = searchParams.get('token');
  useEffect(() => {
    if (isNullish(gcpToken)) {
      void navigate('/', { replace: true });
      notify.addNotification({
        timeout: 3000,
        title: 'Invalid URL',
        description: 'Missing query parameter.',
        type: 'warning',
      });
    }
  }, [gcpToken, navigate, notify]);
  if (isNullish(gcpToken)) {
    return null;
  }

  return <ISaasMarketplaceRegistrationWithoutOrgSelector gcpToken={gcpToken} />;
};

/* TODO: This is to be uncommented and reimplemented as soon as we have Project CRUD.

const formatOptionLabel = ({ value: org }: { key: string; value: Organization }, { context }: { context: string }) => {
  if (context === 'value') {
    return <div className="flex flex-row whitespace-break-spaces">{org.displayName}</div>;
  } else if (context === 'menu') {
    return (
      <div className="flex grow flex-col justify-between gap-1">
        <span className="w-1/2">{org.displayName}</span>
        <span className="text-neutral-text-weaker break-words">{org.id}</span>
      </div>
    );
  }
  return null;
};

const ISaaSOrgSelectorDialog = ({
  orgs,
  onSubmit,
  onClose,
  selectedOrg,
  onSelectOrg,
  isLoading,
}: {
  orgs: Organization[];
  onSubmit: () => void;
  onClose: () => void;
  onSelectOrg: (org?: Organization) => void;
  selectedOrg: Organization | null;
  isLoading: boolean;
}) => {
  return (
    <Dialog isOpen onClose={onClose}>
      <Dialog.Header>Connect GCP Marketplace order</Dialog.Header>
      <Dialog.Description>Select which org you want to place your new marketplace project.</Dialog.Description>
      <Dialog.Content>
        <Select
          type="select"
          selectProps={{
            menuPosition: 'fixed',
            placeholder: 'Select',
            options: orgs.map((org) => ({ key: org.id, value: org })),
            value: isNotNullish(selectedOrg) ? { key: selectedOrg.id, value: selectedOrg } : undefined,
            formatOptionLabel,
            onChange: (value) => onSelectOrg(value?.value),
          }}
          size="medium"
          label="Target Organization"
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Button onClick={onClose} color="neutral" fill="outlined" isDisabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={onSubmit} isDisabled={isNullish(selectedOrg)} isLoading={isLoading}>
          Confirm
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export const ISaasMarketplaceRegistrationWithOrgSelector = () => {
  const navigate = useNavigate();
  const notify = useNotifications();
  const userDetailsRes = consoleApi.useGetUserDetailsQuery();
  const { data: orgs = [], isLoading: isLoadingOrgs } = consoleApi.useListOrganizationsByUserQuery(
    userDetailsRes.data?.id ?? skipToken,
  );
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [searchParams] = useSearchParams();
  const gcpToken = searchParams.get('gcp-marketplace-token');
  const filteredOrgs =
    userDetailsRes.data?.featureToggles['console-remove-plantype'] === false
      ? orgs.filter((org) => org.planType === PLAN_TYPE.SELF_SERVE)
      : [];
  const [verifyOrder, verifyOrderRes] = consoleApi.useFindISaasOrderMutation();
  const [registerOrder, registerOrderRes] = consoleApi.useRegisterISaasOrderMutation();

  useEffect(() => {
    if (isNotNullish(gcpToken)) {
      verifyOrder(gcpToken)
        .unwrap()
        .then((status) => {
          if (status !== 'available') {
            const notFoundMessage = 'Your marketplace order could not be found. Please try again later.';
            const consumedMessage =
              'This order has already been processed. Ask your Project admin to add you to the project in order to 
              continue.';
            notify.addNotification({
              timeout: 5000,
              title: '',
              description: status === 'registered' ? consumedMessage : notFoundMessage,
              type: 'info',
            });
            void navigate('/', { replace: true });
          }
        })
        .catch((err: FetchBaseQueryError | SerializedError) => {
          const message = getApiErrorMessage(err);
          notify.addNotification({
            timeout: 5000,
            title: 'Error',
            description: message,
            type: 'warning',
          });

          void navigate('/', { replace: true });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNullish(gcpToken)) {
      notify.addNotification({
        timeout: 3000,
        title: 'Invalid URL',
        description: 'Missing query parameter.',
        type: 'warning',
      });
      void navigate(`/`, { replace: true });
    }
    if (!isLoadingOrgs && filteredOrgs.length === 0) {
      notify.addNotification({
        timeout: 5000,
        title: 'No available organizations',
        description:
          'There are no available organizations that your new marketplace project can exist in. Try login with another 
          user to complete the signup process',
        type: 'warning',
      });
      void navigate(`/`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = () => {
    if (isNotNullish(gcpToken) && isNotNullish(selectedOrg)) {
      registerOrder({ token: gcpToken, organizationId: selectedOrg.id })
        .unwrap()
        .then(({ orgId }) => {
          void navigate(`/org/${orgId}/projects`, { replace: true });
        })
        .catch((err: FetchBaseQueryError | SerializedError) => {
          const message = getApiErrorMessage(err);
          notify.addNotification({
            timeout: 5000,
            title: 'Error',
            description: message,
            type: 'warning',
          });

          void navigate('/', { replace: true });
        });
    }
  };

  if (verifyOrderRes.isError) {
    // We should handle this later?
  }

  return (
    <Center className="flex-col">
      <LoadingSpinner size="large" />
      {isNotNullish(gcpToken) && !isLoadingOrgs && filteredOrgs.length > 0 && verifyOrderRes.data === 'available' && (
        <ISaaSOrgSelectorDialog
          isLoading={registerOrderRes.isLoading}
          onSubmit={handleSubmit}
          onClose={() => {
            void navigate(`/`, { replace: true });
          }}
          orgs={filteredOrgs}
          onSelectOrg={(org?: Organization) => {
            setSelectedOrg(org ?? null);
          }}
          selectedOrg={selectedOrg}
        />
      )}
    </Center>
  );
};
*/
